import * as moment from "moment";
import * as Highcharts from "highcharts";
import { IChart, ISerie, IWidget, IndexFrontend } from "../shared/models/seriesInfo.interfaces";

/**
 * Gets the correct weather state icon dependenign on the value requested
 * @param stateValue 
 * @returns 
 */
export function getWeatherStateIcon(stateValue: number) : string {
    const statesMapping = {
        1: 'wi wi-day-sunny',
        2: 'wi wi-day-sunny-overcast',
        3: 'wi wi-day-cloudy',
        4: 'wi wi-cloudy',
        6: 'wi wi-day-sprinkle',
        9: 'wi wi-showers',
        10: 'wi wi-hail',
        11: 'wi wi-rain',
    };
    // Check if the stateValue exists in the statesMapping, if not return an empty string
    return statesMapping[stateValue] || '';
}

/**
 * Gets the correct conversion rate for the units requested
 * @param fromUnit 
 * @param toUnit 
 * @returns 
 */
export function getConversionRate(fromUnit: string, toUnit: string): number {
    let conversionRates = {
      'nós to km/h': 1.852,
      'km/h to nós': 0.539957,
      'nós to m/s': 0.514444,
      'm/s to nós': 1.94384,
      'm/s to km/h': 3.6,
      'km/h to m/s': 0.277778
    };

    return conversionRates[`${fromUnit} to ${toUnit}`] || 1;
}

/**
 * Shared x axis plot lines across all charts
 */
export function getXAxisPlotLines() : any[] {
    moment.locale("pt")
    return [{
        // Style
        color: '#fff',
        width: 1.5,
        dashStyle: 'longdashdot',
        rotation: 0,
        // Labels
        label: {
            text: moment().format('HH:mm'),
            style: { color: '#fff' },
            rotation: 270,
            y: 45,
            x: 15,
            verticalAlign: 'top',
        },
        zIndex: 5,
        value: moment(),
    }];
}

/**
 * Creates the series name based on the isMeasure flag
 * @param serie 
 * @returns 
 */
export function getSeriesName(serie: ISerie) : string {
    return serie.description; 
}

/**
 * Replaces the unit placeholder
 * @param description 
 * @param unit 
 * @param searchValue 
 * @returns 
 */
export function replaceUnitPlaceholder(
    description: string, 
    unit: string, 
    searchValue: { [Symbol.replace](string: string, replaceValue: string) }
) {
    return description.replace(searchValue, `[${unit}]`);
}

/**
 * Returns all the y axis needed for the chart to be draw
 * @param chart 
 * @param expectedUnits 
 */
export function getYAxis(chart: IChart, ...expectedUnits: string[]) : any[] {
    // Create the y axis, each y axis configured can have multiple series
    let yAxis: any[] = [];
    chart.yAxis.forEach(axis => {
        
        // Creating the ticks based on the min, max and tick interval requested
        let isDynamicAxis = axis.tickInterval === "dynamic";
        let ticks = [];
        
        if (!isDynamicAxis) {
            let numTicks = Math.ceil((axis.max - axis.min) / +axis.tickInterval);
        
            // Loop to create ticks that include the min, max, and tickInterval
            for (let i = 0; i <= numTicks; i++) {
                let tickValue = axis.min + (+axis.tickInterval * i);
                if (tickValue <= axis.max) {
                    ticks.push(tickValue);
                }
            }
        
            // Ensure the last tick matches the max value (if rounding issues occur)
            if (ticks[ticks.length - 1] !== axis.max) {
                ticks.push(axis.max);
            }
        }
        
        yAxis.push({
            // Unit & Margin
            unit: axis.series[0].unit,
            margin: -10,
            // Title
            title: {
                text: replaceUnitPlaceholder(axis.name, axis.series[0].unit, /\$unit\$/g),
                margin: 10,
                style: {
                    color: axis.series[0].chartOptions.color,
                    fontSize: 13
                }
            },
            // Labels
            labels: {
                style: {
                    color: axis.series[0].chartOptions.color,
                    fontSize: 13
                },
                formatter: function () { return Highcharts.numberFormat(this.value, 1) },
                format: axis.series[0].decimalPlaces 
                    ? `value:,.${axis.series[0].decimalPlaces}f`
                    : ''
            },
            // Lines
            lineColor: axis.series[0].chartOptions.color,
            tickColor: axis.series[0].chartOptions.color,
            minorTickColor: axis.series[0].chartOptions.color,
            gridLineColor: 'rgba(255,255,255,0.2)',
            gridLineWidth: 0.5,
            // Minimum and Maximum
            tickPositions: isDynamicAxis ? undefined : ticks,
            min: axis.min || 0,
            max: axis.max || undefined,
            // Other properties
            opposite: expectedUnits.includes(axis.series[0].unit),
        });
    });

    return yAxis;
}

/**
 * Parses the data of a serie to make it ready for a chart
 * @param serie 
 * @returns 
 */
export function addDataToChart(serie: ISerie) : number[][] {
    // Returns an array of time series values with associated date and value
    let chartData = serie.chartValues.map((data) => [data.date.getTime(), parseFloat(data.value.toFixed(2))]);

    return chartData
        .filter((data) => data[0] > moment().subtract(serie.visibleHindcastInHours, 'h').toDate().getTime());
}

/**
 * Returns the serie configured for the chart
 * @param serie 
 * @param axisIndex 
 */
export function configureSeries(serie: ISerie, axisIndex: number) : any {
    return {
        // Base settings
        id: serie.id,
        name: getSeriesName(serie),
        yAxis: axisIndex,
        lineWidth: serie.chartOptions.hasDots ? 0 : 2,
        type: serie.chartOptions.isFilled ? 'area' : 'line',
        marker: 
        { 
            enabled: serie.chartOptions.hasDots,
            symbol: serie.chartOptions.hasDots ? serie.chartOptions.symbol : undefined,
            radius: 3
        },
        tooltip: { valueSuffix: ` ${serie.unit}` },
        connectNulls: true,
        dashStyle: !serie.chartOptions.isFilled 
            ? serie.chartOptions.dashStyle ? serie.chartOptions.dashStyle : undefined
            : undefined,
        // Z index
        zIndex: serie.chartOptions.zIndex || undefined,
        // Color or gradient
        color: !serie.chartOptions.isFilled
            ? serie.chartOptions.color
            : {
                linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                stops: [
                    [0, serie.chartOptions.color],
                    [1, Highcharts.color(serie.chartOptions.color).setOpacity(0.3).get('rgba')],
                ],
            },
    }
}

/**
 * Creates the chart requested
 * @param chart 
 * @param seriesSettings 
 * @param chartSettings 
 * @param expectedUnits 
 */
export function createChart(chart: IChart, seriesSettings: any[], chartSettings: any, ...expectedUnits: string[]) {
    // Common chart settings
    chartSettings.xAxis.max = moment().add(48, 'h').toDate();
    chartSettings.tooltip = { 
        shared: true, 
        crosshairs: {
            width: 0.5,
            dashStyle: 'line'
        },
    }

    // y axis settings
    chartSettings.yAxis = getYAxis(chart, ...expectedUnits);
    
    // x axis plot lines configuration
    chartSettings.xAxis.plotLines = getXAxisPlotLines();

    // x axis formatter for the ticks
    chartSettings.xAxis.labels = {
        formatter: function () {
            if (this.isLast) {
                return null; // Hide the last label
            }
            return Highcharts.dateFormat('%d/%m %H:%M', this.value);
        },
        style: {
            fontSize: '13px',                 // Increase size
            fontWeight: '750',                // Make text thicker
            color: '#ffffff'                // Optional: make sure it's readable on your background
        }
    }

    // Adjust legend:
    chartSettings.legend = {
        itemStyle: {
            color: '#dddddd',
            fontWeight: '600'
        },
        itemHoverStyle: {
            color: '#ffffff',
        },
        itemHiddenStyle: {
            color: '#777777'
        }
    };
    
    // Add series data to chart
    let i = 0;
    chart.yAxis.forEach((axis, yAxisIndex) => {
        axis.series.forEach((serie) => {
            if (serie.chartValues) {
                seriesSettings[i] = configureSeries(serie, yAxisIndex);
                seriesSettings[i].data = addDataToChart(serie);
                i++;
            }
        });
    })
}

/**
 * Parses a widget configuration to a map, being the key the index frontend of the data to be displayed
 * @param widget 
 * @returns 
 */
export function getMappedData(widget: IWidget) : Map<IndexFrontend, ISerie[]> {
    let map: Map<IndexFrontend, ISerie[]> = new Map<IndexFrontend, ISerie[]>();

    // Getting chart series
    widget.charts.yAxis.forEach(axis => { axis.series.forEach(serie => addSerieToMap(map, serie)) });
    
    // Get last values series
    widget.lastValues.forEach(serie => addSerieToMap(map, serie));

    // Getting table series
    if (widget.tables && widget.tables.length > 0) {
        widget.tables.forEach(serie => addSerieToMap(map, serie));
    }

    return map;
}

/**
 * Adds a series to a map
 * @param map 
 * @param serie 
 */
export function addSerieToMap(map: Map<IndexFrontend, ISerie[]>, serie: ISerie) : void {
    if (map.has(serie.indexFrontend)) {
        map.get(serie.indexFrontend).push(serie);
    }
    else {
        map.set(serie.indexFrontend, [serie]);
    }
}